import clsx from "clsx";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Badge,
  HyphenIcon,
  Loader,
  NotificationStack,
  NotificationType,
  OverflowText,
} from "@transfr-inc/dashboard-components";
import { Battery } from "@transfr-inc/dashboard-components/tiles";
import { formatDateWithTime } from "@transfr-inc/dashboard-components/utils";

import { useNotificationStack } from "@lib/hooks";
import { XAPI_OBJECT_TYPES } from "@models/tracking/event-types";
import { ExpandAccordionEvent } from "@models/tracking/xapi-events";
import {
  ACTIVITY_CONFIG,
  STATUS_CONFIG,
} from "@views/devices/models/constants";

import { CastingLink } from "./casting-link";
import { DeviceAppsTable } from "./device.app-table";
import { DeviceTags } from "./device.tags";

import "./devices.accordion.scss";

export function DeviceAccordion({
  device,
  configurations,
  className,
  isOpen,
  responsiveStyles,
  tracking,
  deviceConfigErrorIds = [],
  showLastOnline = false,
  organizationDeviceTags,
  updateDeviceTags,
  onCreateTag,
}) {
  const { notifications, closeNotification, addNotification } =
    useNotificationStack();

  const hasConfigError = deviceConfigErrorIds.includes(device?.configurationId);

  const activity = device?.online
    ? ACTIVITY_CONFIG.ACTIVE
    : ACTIVITY_CONFIG.INACTIVE;
  const status = STATUS_CONFIG[device?.status.toUpperCase()];

  const findConfigurationDataForDevice = () => {
    if (!configurations || configurations.length === 0) {
      return;
    }

    return configurations.find(
      (config) => config.configurationId === device?.configurationId
    );
  };

  const configurationData = findConfigurationDataForDevice();

  const addNotificationIfConfigError = () => {
    if (hasConfigError && notifications.length === 0) {
      addNotification({
        type: NotificationType.error,
        message:
          "Failed to retrieve configuration details for this device. Please try reloading the page. If this issue persists please contact customer support",
        autoClose: false,
      });
    }
  };

  return (
    <Accordion
      className={className}
      isOpen={isOpen}
      onExpand={() => {
        if (!isOpen) {
          addNotificationIfConfigError();
          tracking.trackEvent(
            new ExpandAccordionEvent(
              device.id,
              `headset_${device.name}`,
              XAPI_OBJECT_TYPES.ACCORDION
            )
          );
        }
      }}
    >
      <AccordionHeader>
        <AccordionHeaderName>
          <OverflowText text={device?.name} className="device-column">
            {device?.name}
          </OverflowText>
          <div className="device-column battery">
            <Battery
              batteryPercentage={device?.batteryLevel}
              batteryIsCharging={device?.batteryIsCharging}
              hideLowTextWarning
            />
            {device?.batteryController0 != null && (
              <Battery
                batteryPercentage={device?.batteryController0}
                batteryIsCharging={device?.batteryIsCharging}
                label={"L"}
                hideBatteryPercentage
                hideLowTextWarning
              />
            )}
            {device?.batteryController1 != null && (
              <Battery
                batteryPercentage={device?.batteryController1}
                batteryIsCharging={device?.batteryIsCharging}
                label={"R"}
                hideBatteryPercentage
                hideLowTextWarning
              />
            )}
          </div>
          <div className="device-column activity">
            {activity.text}
            <div className={clsx("status-dot", activity.className)} />
          </div>
          {showLastOnline && (
            <OverflowText text={device?.lastOnline} className="device-column">
              {formatDateWithTime(device?.lastOnline)}
            </OverflowText>
          )}
          <div className="device-column">
            <OverflowText text={device?.location} className="device-column">
              {device?.location ?? <HyphenIcon />}
            </OverflowText>
          </div>
          <div className="device-column">
            {device?.firmwareVersion ?? <HyphenIcon />}
          </div>
          <div className="device-column device-status">
            <Badge value={status.text} className={status.className} />
          </div>
          <DeviceTags
            className="device-column"
            values={device?.tags}
            availableValues={organizationDeviceTags}
            onChange={updateDeviceTags}
            onCreateTag={onCreateTag}
          />
          <CastingLink className="device-column" href={device?.castingLink} />
        </AccordionHeaderName>
      </AccordionHeader>
      <AccordionBody className="device-accordion-body">
        {hasConfigError ? (
          <div>
            <NotificationStack
              notifications={notifications}
              onClose={(index) => closeNotification({ index })}
              animated
            />
          </div>
        ) : configurationData ? (
          <DeviceAppsTable
            data={configurationData.apps}
            responsiveStyles={responsiveStyles}
          />
        ) : (
          <Loader />
        )}
      </AccordionBody>
    </Accordion>
  );
}
